/**
 * Search Rewrite Manager - Moon Moon Agency
 * Réécrit le terme de recherche avant de l'envoyer à Shopify :
 * - formes collées à un chiffre (« osb3 » → « osb 3 »), codées ici ;
 * - synonymes (« bastaing » → « poutre »), lus dans le métaobjet `reecriture_recherche`
 *   (Admin > Contenu > Métaobjets), exposés par le snippet mm-search-rewrite.
 * Le terme d'origine part dans le paramètre `tape` de l'URL, pour le suivre dans GA4.
 */

/*********************************************************************************************
 * 🔎 SEARCH REWRITE MANAGER
 */
const SearchRewriteManager = {
	config: {
		selectors: {
			rewrites: '#mm-search-rewrites',
			searchInput: 'input[name="q"]',
		},
		// Préfixes réécrits quand un chiffre leur est collé : « osb3 », « cp18 », « classe4 », « cl4 »
		gluedPrefixes: ['osb', 'cp', 'classe', 'cl'],
		// Abréviations développées seulement devant un chiffre : « cl4 », « cl 4 » → « classe 4 »
		prefixAliases: { cl: 'classe' },
		originalParam: 'tape',
		searchPath: /\/search\/?$/,
	},

	rewrites: [],

	/**
	 * Initialize the module
	 */
	init() {
		this.rewrites = this._readRewrites();
		this._redirectSearchPage();
		this._bindEvents();
	},

	/**
	 * Réécrit un terme de recherche, sans tenir compte des majuscules ni des accents.
	 * Renvoie le terme d'origine tel quel si aucune règle ne s'applique.
	 * @param {string} query
	 * @returns {string}
	 */
	rewrite(query) {
		const normalized = this._normalize(query);
		const glued = new RegExp(`(^|[^a-z0-9])(${this.config.gluedPrefixes.join('|')})[ -]?(\\d)`, 'g');
		let result = normalized.replace(glued, (match, before, prefix, digit) => `${before}${this.config.prefixAliases[prefix] || prefix} ${digit}`);

		this.rewrites.forEach(({ from, to }) => {
			const pattern = new RegExp(`(^|\\s)${this._escape(this._normalize(from))}(?=\\s|$)`, 'g');
			result = result.replace(pattern, `$1${to}`);
		});

		return result === normalized ? query : result;
	},

	_readRewrites() {
		const script = document.querySelector(this.config.selectors.rewrites);
		if (!script) return [];

		try {
			return JSON.parse(script.textContent).filter((rewrite) => rewrite.from && rewrite.to);
		} catch (error) {
			return [];
		}
	},

	// Adresse /search?q=osb3 ouverte directement (lien, Google) : une seule redirection, marquée par `tape`
	_redirectSearchPage() {
		if (!this.config.searchPath.test(window.location.pathname)) return;

		const url = new URL(window.location.href);
		const query = url.searchParams.get('q');
		if (!query || url.searchParams.has(this.config.originalParam)) return;

		const rewritten = this.rewrite(query);
		if (rewritten === query) return;

		url.searchParams.set('q', rewritten);
		url.searchParams.set(this.config.originalParam, query);
		window.location.replace(url.toString());
	},

	_bindEvents() {
		document.addEventListener('submit', (e) => {
			const form = e.target;
			const input = form.querySelector(this.config.selectors.searchInput);
			if (!input || !this.config.searchPath.test(new URL(form.action, window.location.origin).pathname)) return;

			const query = input.value;
			const rewritten = this.rewrite(query);
			if (rewritten === query) return;

			input.value = rewritten;
			this._setOriginalField(form, query);
		});
	},

	_setOriginalField(form, query) {
		let field = form.querySelector(`input[name="${this.config.originalParam}"]`);
		if (!field) {
			field = document.createElement('input');
			field.type = 'hidden';
			field.name = this.config.originalParam;
			form.appendChild(field);
		}
		field.value = query;
	},

	_normalize(text) {
		return text
			.toLowerCase()
			.normalize('NFD')
			.replace(/[̀-ͯ]/g, '')
			.replace(/\s+/g, ' ')
			.trim();
	},

	_escape(text) {
		return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	},
};

// Exposé pour predictive-search.js, qui réécrit aussi le terme des suggestions
window.SearchRewriteManager = SearchRewriteManager;

// Auto-init avec vérification readyState
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => SearchRewriteManager.init());
} else {
	SearchRewriteManager.init();
}
